(() => {
  const COMPLETE_PARAM = 'makerbetter_login_complete';
  const COMPLETE_EVENT = 'makerbetter:shopify-login-complete';
  const STORAGE_KEY = 'makerbetter_shopify_login_complete';
  const POPUP_SESSION_KEY = 'makerbetter_shopify_login_popup';
  const POPUP_NAME = 'makerbetter-shopify-login';
  const ACCOUNT_DIALOG_SELECTOR = 'drawer-element.makerbetter-account-dialog';

  let activePopup = null;
  let popupClosedTimer = null;
  let reloadScheduled = false;
  const transitionFallbackTimers = new WeakMap();

  const parseTransitionTime = (value) => {
    return value.split(',').reduce((longest, item) => {
      const match = item.trim().match(/^([\d.]+)(ms|s)$/);
      if (!match) return longest;

      const milliseconds = Number(match[1]) * (match[2] === 's' ? 1000 : 1);
      return Math.max(longest, milliseconds);
    }, 0);
  };

  const clearTransitionFallback = (dialog) => {
    const timer = transitionFallbackTimers.get(dialog);
    if (timer) window.clearTimeout(timer);
    transitionFallbackTimers.delete(dialog);
  };

  const scheduleTransitionFallback = (dialog) => {
    clearTransitionFallback(dialog);

    const overlay = dialog.querySelector('.makerbetter-account-dialog__overlay');
    if (!overlay) return;

    // The theme waits for this event before restoring the drawer and scroll lock.
    const computedStyle = window.getComputedStyle(overlay);
    const transitionTime = parseTransitionTime(computedStyle.transitionDuration) + parseTransitionTime(computedStyle.transitionDelay);
    const timeout = Math.max(320, transitionTime + 120);
    const timer = window.setTimeout(() => {
      transitionFallbackTimers.delete(dialog);
      overlay.dispatchEvent(new Event('transitionend', { bubbles: true }));
    }, timeout);

    transitionFallbackTimers.set(dialog, timer);
  };

  document.addEventListener('toggle', (event) => {
    if (!(event.target instanceof Element)) return;

    const dialog = event.target.closest(ACCOUNT_DIALOG_SELECTOR);
    if (!dialog) return;

    scheduleTransitionFallback(dialog);
  });

  document.addEventListener('drawer:afterShow', (event) => {
    if (event.target instanceof Element && event.target.matches(ACCOUNT_DIALOG_SELECTOR)) {
      clearTransitionFallback(event.target);
    }
  });

  document.addEventListener('drawer:afterHide', (event) => {
    if (event.target instanceof Element && event.target.matches(ACCOUNT_DIALOG_SELECTOR)) {
      clearTransitionFallback(event.target);
    }
  });

  const scheduleReload = () => {
    if (reloadScheduled) return;

    reloadScheduled = true;
    window.setTimeout(() => window.location.reload(), 120);
  };

  const notifyLoginComplete = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // The popup-close fallback still refreshes the storefront.
    }

    if (window.opener && window.opener !== window) {
      window.opener.postMessage({ type: COMPLETE_EVENT }, window.location.origin);
    }
  };

  const finishPopupReturn = () => {
    const currentURL = new URL(window.location.href);
    if (currentURL.searchParams.get(COMPLETE_PARAM) !== '1') return;

    currentURL.searchParams.delete(COMPLETE_PARAM);
    window.history.replaceState({}, document.title, `${currentURL.pathname}${currentURL.search}${currentURL.hash}`);

    notifyLoginComplete();

    let openedAsPopup = Boolean(window.opener);
    try {
      openedAsPopup = openedAsPopup || window.sessionStorage.getItem(POPUP_SESSION_KEY) === '1';
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
    if (openedAsPopup) {
      window.setTimeout(() => window.close(), 80);
    }
  };

  const buildLoginURL = (rawLoginURL) => {
    const loginURL = new URL(rawLoginURL, window.location.origin);
    const returnURL = new URL(window.location.href);

    returnURL.searchParams.set(COMPLETE_PARAM, '1');
    loginURL.searchParams.set('return_to', `${returnURL.pathname}${returnURL.search}${returnURL.hash}`);

    return loginURL.toString();
  };

  const popupFeatures = () => {
    const width = Math.min(520, Math.max(360, window.screen.availWidth - 32));
    const height = Math.min(760, Math.max(600, window.screen.availHeight - 48));
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);

    return [
      'popup=yes',
      'resizable=yes',
      'scrollbars=yes',
      `width=${Math.round(width)}`,
      `height=${Math.round(height)}`,
      `left=${Math.round(left)}`,
      `top=${Math.round(top)}`,
    ].join(',');
  };

  const openLoginPopup = (rawLoginURL) => {
    const loginURL = buildLoginURL(rawLoginURL);
    const popup = window.open('about:blank', POPUP_NAME, popupFeatures());

    if (!popup) {
      window.location.assign(loginURL);
      return;
    }

    activePopup = popup;
    try {
      popup.sessionStorage.setItem(POPUP_SESSION_KEY, '1');
      popup.opener = null;
    } catch {
      // Same-origin completion signalling is also backed by localStorage.
    }

    popup.location.replace(loginURL);
    popup.focus();

    window.clearInterval(popupClosedTimer);
    popupClosedTimer = window.setInterval(() => {
      if (!activePopup || !activePopup.closed) return;

      window.clearInterval(popupClosedTimer);
      popupClosedTimer = null;
      activePopup = null;
      scheduleReload();
    }, 500);
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-makerbetter-login]');
    if (!button) return;

    event.preventDefault();
    const dialog = button.closest('drawer-element');
    if (dialog && typeof dialog.hide === 'function') {
      dialog.hide();
    }
    openLoginPopup(button.dataset.makerbetterLoginUrl || '/customer_authentication/login');
  });

  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin || event.data?.type !== COMPLETE_EVENT) return;
    scheduleReload();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    scheduleReload();
  });

  window.addEventListener('pagehide', () => window.clearInterval(popupClosedTimer), { once: true });

  finishPopupReturn();
})();
